import React, { useEffect, useState, useRef } from "react";
import Loader from "./loader";
import { TickMark } from "./common";
import * as gtag from "../utils/gtag";

const PreviewWaveform = (props) => {
  const [wavesurfer, setWavesurfer] = useState(null);
  const [isPlaying, setPlaying] = useState(props.isPlaying);
  const [isReady, setReadyState] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(props.hasPlayed);
  const [hasError, setError] = useState(false);
  const waveContainer = useRef(null);
  const audioEl = useRef(null);
  const showTick = props.showTick;
  const [elapsedTime, setElapsedTime] = useState(0);
  const intervalRef = useRef(null);
  const [wavesurferPromise, setWavesurferPromise] = useState<
    Promise<void> | undefined
  >(undefined);

  useEffect(() => {
    let isSet = false;
    if (!props.trackUrl) {
      setError(true);
      return;
    } else {
      setError(false);
    }

    const loadWavesurfer = async () => {
      let _wavesurfer = wavesurfer;
      if (
        !_wavesurfer &&
        waveContainer.current &&
        waveContainer.current.children &&
        waveContainer.current.children.length < 1
      ) {
        const WaveSurfer = (await import("wavesurfer.js")).default;
        const RegionPlugin = (
          await import("wavesurfer.js/dist/plugin/wavesurfer.regions")
        ).default;
        if (!mounted) return;

        let region = RegionPlugin.create({
          name: "region",
          drag: false,
          resize: false,
        });
        _wavesurfer = WaveSurfer.create({
          container: waveContainer.current,
          scrollParent: false,
          backend: "MediaElement",
          media: audioEl.current,
          barWidth: 2,
          height: props.isPreviewer ? 40 : 20,
          barMinHeight: 0.7,
          normalize: true,
          barGap: props.isPreviewer ? 5 : 2,
          center: true,
          interact: false,
          progressColor: "rgba(255,255,255,.9)",
          waveColor: props.showIcons
            ? !hasPlayed
              ? "rgba(255,255,255,.6)"
              : "rgba(255,255,255,1)"
            : "rgba(255,255,255,.6)",
          cursorWidth: 0,
          responsive: true,
        });
        _wavesurfer.addPlugin(region);

        setWavesurfer(_wavesurfer);
      }
      if (_wavesurfer && _wavesurfer !== wavesurfer) {
        _wavesurfer.load(audioEl.current);
        _wavesurfer.on("waveform-ready", function () {
          setReadyState(true);
        });
        _wavesurfer.on("error", function (error) {});
        _wavesurfer.on("finish", function () {
          setPlaying(false);
          if (props.setPreviewPlaying) {
            props.setPreviewPlaying(props.index, false);
          }
          if (props.resetPlay) {
            props.resetPlay();
          }
        });
        _wavesurfer.on("ready", function () {
          const correctDuration = props.isSearch
            ? audioEl && audioEl.current.duration
            : props.optionIndexDuration;
          _wavesurfer.getDuration = () => correctDuration;
          if (props.boundaries && props.showSeletivePopup) {
            _wavesurfer.addRegion({
              start: props.boundaries[0], // time in seconds
              end: props.boundaries[1], // time in seconds
              color: "rgb(105, 99, 141, .55)",
              loop: false,
              multiple: false,
              drag: false,
            });
            _wavesurfer.disableDragSelection();
          }
        });

        _wavesurfer.on("audioprocess", function () {
          let currentTime = _wavesurfer.getCurrentTime();
          if (currentTime > 3 && !isSet) {
            if (props.updatePlayedOnLS) {
              props.updatePlayedOnLS(props.index);
            }
            if (!hasPlayed) {
              setHasPlayed(true);
            }
            isSet = true;
          }
          const correctDuration = props.isSearch
            ? audioEl && audioEl.current.duration
            : props.optionIndexDuration;

          if (correctDuration) {
            const progress = currentTime / correctDuration;

            setElapsedTime(Math.floor(currentTime));

            if (progress <= 1) {
              _wavesurfer.drawer.progress(progress);
            } else {
              _wavesurfer.drawer.progress(1);
            }

            if (currentTime >= correctDuration) {
              if (props.setPreviewPlaying) {
                props.setPreviewPlaying(props.index, false);
              }
              wavesurferPromise?.then(() => _wavesurfer.pause());
            }
          }
        });
      }
    };

    let mounted = true;
    loadWavesurfer();
    return () => {
      mounted = false;
    };
  }, [props.index, props.resetOptions]);

  useEffect(() => {
    if (wavesurfer && props.selectedTrack) {
      wavesurfer.load(audioEl.current);
    }
  }, [props.updatedStemFile]);

  useEffect(() => {
    setHasPlayed(props.hasPlayed);
  }, [props.hasPlayed]);

  useEffect(() => {
    const anyError = !wavesurfer || !props.trackUrl || hasError;
    if (anyError) return;
    if (props.isPlaying && audioEl?.current && isReady) {
      if (props.boundaries && props.showSeletivePopup) {
        setWavesurferPromise(
          wavesurfer.play(props.boundaries[0], props.boundaries[1])
        );
        setPlaying(true);
      } else {
        setWavesurferPromise(wavesurfer.play());
        setPlaying(true);
      }
    } else {
      setPlaying(false);
      wavesurferPromise?.then(() => wavesurfer.pause());
    }
  }, [props.isPlaying, isReady]);

  const updateAltTrack = () => {
    if (props.selectAltTrack) {
      props.selectAltTrack();
    }
    return;
  };

  const handleWaveformClick = (e) => {
    if (audioEl.current && waveContainer.current) {
      const rect = waveContainer.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;

      if (clickX < 0) {
        return;
      }

      const duration = props.isSearch
        ? audioEl && audioEl.current.duration
        : props.optionIndexDuration;

      if (duration > 0) {
        const newTime = Math.max(
          0,
          Math.min((clickX / width) * duration, duration)
        );
        audioEl.current.currentTime = newTime;
        setElapsedTime(newTime / duration);
        wavesurfer.seekTo(newTime / duration);
        props.onSeek(e, newTime);
        gtag.event(
          {
            action: "seek-track",
            category: "preview-screen",
          },
          {
            category: "preview-screen",
            seekValue: Math.round(newTime),
            duration: duration,
          }
        );
      }
    }
  };

  const getDuration = () => {
    if (audioEl && audioEl.current) {
      const duration = props.isSearch
        ? audioEl && audioEl.current.duration
        : props.optionIndexDuration;
      return formatTime(duration);
    }
  };

  const formatTime = (totalSeconds) => {
    totalSeconds = Math.floor(totalSeconds);
    const minutes = Math.floor(totalSeconds / 60) || 0;
    const seconds = totalSeconds % 60 || 0;

    const formattedMinutes = minutes.toString().padStart(2, "0");
    const formattedSeconds = seconds.toString().padStart(2, "0");

    return `${formattedMinutes}:${formattedSeconds}`;
  };

  return (
    <div>
      {props.isPlaceholder || (!isReady && !hasError) ? (
        <div className="absolute text-center text-xs text-white flex items-center h-full w-full flex-column justify-center sm:top-1/2 sm:left-1/2 sm:transform sm:-translate-x-1/2 sm:-translate-y-1/2 ">
          <div className="h-4 w-4 mr-1">
            <Loader color="#aaa" />
          </div>
          {!props.isPlaceholder && !props.showIcons && !props.hideLoader ? (
            <span className="inline-block">Loading Preview</span>
          ) : (
            <></>
          )}
        </div>
      ) : (
        <></>
      )}

      {props.trackUrl && props.trackUrl !== "" && (
        <div
          className={
            "flex cursor-pointer flex-row p-2 items-center " +
            (props.selectedTrack && !props.isPreviewer && " bg-onyx rounded-lg")
          }
        >
          {props.isPreviewerScreen && (
            <p className="text-white opacity-60 mr-2">
              {formatTime(elapsedTime)}
            </p>
          )}
          <div
            className="el-waveform mr-2 overflow-hidden z-10 select-none h-full w-full"
            onClick={(e) => {
              updateAltTrack();
              if (props.isPreviewerScreen && props.selectedTrack) {
                handleWaveformClick(e);
              }
            }}
            ref={waveContainer}
          />
          <audio
            playsInline
            ref={audioEl}
            crossOrigin="anonymous"
            src={props.trackUrl}
          />
          {props.isPreviewerScreen && (
            <p className="text-white opacity-60 mr-2 ml-1">
              {audioEl && getDuration()}
            </p>
          )}
          {props.showIcons && props.selectedTrack && (
            <div class={"border rounded-full w-5px h-5px p-px border-green"}>
              <TickMark active={true} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

let waveFormMemo = React.memo(PreviewWaveform, (prev, next) => {
  if (prev.trackUrl !== next.trackUrl && next.isPlaying) {
    return true;
  }
});

export default waveFormMemo;
